// src/utils/invoiceGeneratorV2.js
import jsPDF from "jspdf";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import {
  fetchCBUAEConversionRate,
  fetchConversionRate,
} from "./invoiceGenerator";

const DEFAULT_INVOICING_CONFIG = {
  version: 1,
  profiles: {
    UK: {
      header: { logoUrl: "invoice_logo.png" },
      company: {
        name: "Fort Corps Limited",
        addressLines: [
          "Belsyre Court",
          "First Floor, 57 Woodstock Road",
          "Oxford, England, OX2 6HJ",
        ],
        phone: "+44 186 523 8285",
        email: "zak@fortcorps.com",
        trn: "",
      },
      invoiceLabels: {
        invoiceDate: "Invoice Date",
        invoiceNo: "Invoice No.",
        projectNo: "Project No.",
        projectRef: "Your Project Ref",
        orderedBy: "Ordered by",
      },
      payment: {
        heading: "Payment Details",
        accountHolder: "Fort Corps Limited",
        bankLines: ["Barclays Bank, 54 Cornmarket Street, Oxford, OX1 3HB"],
        sortCode: "20-65-20",
        accountNo: "23954005",
        iban: "GB56BUKB20652023954005",
        swift: "BUKBGB22",
        terms: "30 days",
        note: "Any queries about this invoice should be raised within seven days of its receipt.",
      },
      footer: {
        line: "Fort Corps Limited | Company Number 13497195 | VAT Registration Number 404510152 | Belsyre Court, First Floor, 57 Woodstock Road, Oxford, England, OX2 6HJ",
        contact: "Tel: +44 186 523 8285 | Email: zak@fortcorps.com",
      },
    },
    UAE: {
      header: {
        logoUrl: "uae_logo_invoice.png",
        logoUrlTax: "uae_logo_tax_invoice.png",
      },
      company: {
        name: "Fort Core LLC FZ",
        addressLines: [
          "Meydan Grandstand, 6th Floor",
          "Meydan Road, Nad Al Sheba",
          "Dubai, United Arab Emirates",
        ],
        phone: "+971 4 368 7580",
        email: "zak@fortcore.com",
        trn: "104247542400003",
      },
      invoiceLabels: {
        invoiceDate: "Invoice Date",
        invoiceNo: "Invoice No.",
        projectNo: "Project No.",
        projectRef: "Your Project Ref",
        orderedBy: "Ordered by",
      },
      payment: {
        heading: "Payment Details",
        accountHolder: "Fort Core LLC FZ",
        bankLines: ["Abu Dhabi Islamic Bank, Arenco Tower, Dubai Media City"],
        sortCode: "N/A",
        accountNo: "19186681",
        iban: "AE210500000000019186681",
        swift: "ABDIAEADXXX",
        terms: "30 days",
        note: "Any queries about this invoice should be raised within seven days of its receipt.",
      },
      footer: {
        line: "Fort Core LLC FZ | Tax Reg Number 104247542400003 | VAT Reg Number 103000956680 | Meydan Grandstand, 6th Floor, Meydan Road, Nad Al Sheba, Dubai, United Arab Emirates",
        contact: "Tel: +971 4 368 7580 | Email: zak@fortcore.com",
      },
    },
  },
};

const getAssetUrl = (fileName) =>
  fileName?.startsWith("http")
    ? fileName
    : `${window.location.origin}/${fileName}`;

const fetchInvoicingConfig = async () => {
  try {
    const snap = await getDoc(doc(db, "appConfig", "invoicing"));
    if (snap.exists()) {
      return snap.data();
    }
  } catch (err) {
    console.error("Failed to load invoicing config, using defaults", err);
  }
  return DEFAULT_INVOICING_CONFIG;
};

const fetchClientByName = async (clientName) => {
  if (!clientName) return {};
  try {
    const clientsCollection = collection(db, "clients");
    const clientQuery = query(
      clientsCollection,
      where("clientName", "==", clientName)
    );
    const clientSnapshot = await getDocs(clientQuery);
    return clientSnapshot.empty ? {} : clientSnapshot.docs[0].data();
  } catch (err) {
    console.error("Failed to load client data", err);
    return {};
  }
};

const loadImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load image:", url, err);
    return null;
  }
};

const transliterateTurkish = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/[İİıŞşĞğÇçÖöÜü]/g, (ch) => {
    switch (ch) {
      case "İ":
      case "İ":
        return "I";
      case "ı":
        return "i";
      case "Ş":
        return "S";
      case "ş":
        return "s";
      case "Ğ":
        return "G";
      case "ğ":
        return "g";
      case "Ç":
        return "C";
      case "ç":
        return "c";
      case "Ö":
        return "O";
      case "ö":
        return "o";
      case "Ü":
        return "U";
      case "ü":
        return "u";
      default:
        return ch;
    }
  });
};

const normalizePdfWhitespace = (value) =>
  String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\u200B\uFEFF\t]/g, " ");
const collapseSpacedLine = (line) => {
  const trimmed = line.trim();
  if (!trimmed || !/ {2,}/.test(line)) return line;
  const tokens = trimmed.split(/ +/);
  if (tokens.length < 6) return line;
  const singleCharCount = tokens.filter((token) => token.length === 1).length;
  const avgTokenLength =
    tokens.reduce((sum, token) => sum + token.length, 0) / tokens.length;
  const looksLetterSpaced =
    singleCharCount / tokens.length >= 0.7 && avgTokenLength <= 1.4;
  if (!looksLetterSpaced) return line;
  return trimmed
    .split(/ {2,}/)
    .map((segment) => segment.replace(/ /g, ""))
    .join(" ");
};
// Normalize odd whitespace and collapse letter-spaced lines before PDF layout.
const normalizePdfText = (value) =>
  normalizePdfWhitespace(value)
    .split("\n")
    .map((line) => collapseSpacedLine(line))
    .join("\n");
const safeText = (value) => transliterateTurkish(normalizePdfText(value));

const pxToMm = (px) => (px * 25.4) / 96;
const pxToPt = (px) => (px * 72) / 96;
const splitLines = (doc, text, width) =>
  doc.splitTextToSize(safeText(text), width < 5 ? 5 : width);
const measureLinesHeight = (lineCount, lineHeight) => lineCount * lineHeight;
const toNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};
const toMoney = (val) =>
  Math.round((toNumber(val) + Number.EPSILON) * 100) / 100;
const nowMs = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

// Simple retry helper for transient failures (e.g. network/auth hiccups)
const retryWithBackoff = async (
  label,
  fn,
  retries = 2,
  initialDelayMs = 350
) => {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (err) {
      attempt += 1;
      if (attempt > retries) {
        console.error(`[invoice-v2] ${label} failed after ${attempt} attempts`, err);
        throw err;
      }
      const backoff = initialDelayMs * attempt;
      console.warn(
        `[invoice-v2] ${label} attempt ${attempt} failed, retrying in ${backoff}ms`,
        err
      );
      await new Promise((resolve) => setTimeout(resolve, backoff));
    }
  }
};

// Generate a short unique ID to prevent storage path collisions
const generateShortUUID = () => {
  // Use crypto.randomUUID if available (modern browsers), otherwise fallback
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  }
  // Fallback: timestamp + random characters
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const randomPart = Array.from({ length: 8 })
    .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
    .join("");
  return `${Date.now().toString(36)}${randomPart}`;
};
const timedAsync = async (label, fn) => {
  const started = nowMs();
  const result = await fn();
  console.info(
    `[invoice-v2] ${label} took ${(nowMs() - started).toFixed(1)}ms`
  );
  return result;
};

const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value === "number") return new Date(value);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildClientAddress = (clientData = {}) => {
  const trn =
    clientData.taxRegistrationNumber !== null &&
    clientData.taxRegistrationNumber !== undefined
      ? String(clientData.taxRegistrationNumber).trim()
      : "";
  const trnLine = trn ? `TRN: ${trn}` : "";

  return [
    clientData.addressLine1,
    clientData.addressLine2,
    clientData.addressLine3,
    clientData.city,
    clientData.postCode,
    clientData.country,
    trnLine,
  ]
    .filter(Boolean)
    .join("\n");
};

const sanitizeForFileName = (value) =>
  (value || "")
    .toString()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);

const generateCombinedInvoiceNumber = (projectRefs = []) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const alphabet = `${letters}0123456789`;
  // Ensure the random suffix contains at least one letter to avoid all-numeric IDs
  const makePart = () =>
    Array.from({ length: 3 })
      .map(() => alphabet.charAt(Math.floor(Math.random() * alphabet.length)))
      .join("");
  let randomPart = makePart();
  if (!/[A-Z]/.test(randomPart)) {
    const forcedLetter = letters.charAt(Math.floor(Math.random() * letters.length));
    const remaining = makePart().slice(0, 2);
    randomPart = `${forcedLetter}${remaining}`;
  }
  return `INV${randomPart}2025`;
};

const renderInvoicePdf = async ({
  projectId,
  projectData,
  clientData = {},
  invoiceChoice,
  selectedCompany,
  formatMoney,
  formatMoneyWithoutPoundSign,
  useManualAED = false,
  showGBP = true,
  exchangeRateSource = "ExchangeRate-API",
  includeProjectColumn = false,
  projectsIncluded = [],
  invoiceDateOverride = null,
  invoiceNoOverride = null,
  extraMetadata = {},
  storageFolder,
  precomputedTotals = null,
}) => {
  const overallStart = nowMs();

  const clientAddress = buildClientAddress(clientData);

  const chosenInvoiceDate =
    invoiceDateOverride ?? projectData.dateForInvoice ?? null;
  const dateObject = normalizeDateValue(chosenInvoiceDate);
  const formattedDate = !dateObject
    ? ""
    : dateObject.toLocaleDateString("en-GB");

  const projectTasks = projectData.tasks || [];
  const fallbackVatRate = toNumber(projectData.vat);

  let totalAmount = 0;
  let vatAmount = 0;
  let totalIncVat = 0;
  let labelVatRate = fallbackVatRate;
  let vatLabel = "VAT";
  let conversionRate = null;
  let cbuaeRateForNote = null;
  let totalAed = 0;
  let vatAedAmount = 0;
  let totalIncVatAed = 0;

  if (precomputedTotals) {
    totalAmount = toMoney(precomputedTotals.totalAmount ?? 0);
    vatAmount = toMoney(precomputedTotals.vatAmount ?? 0);
    totalIncVat = toMoney(precomputedTotals.totalIncVat ?? totalAmount + vatAmount);
    labelVatRate = toNumber(precomputedTotals.labelVatRate ?? fallbackVatRate);
    vatLabel = precomputedTotals.vatLabel || (labelVatRate > 0 ? `VAT @ ${labelVatRate}%` : "VAT");
    conversionRate =
      toNumber(precomputedTotals.conversionRate) ||
      (exchangeRateSource === "CBUAE"
        ? await fetchCBUAEConversionRate()
        : await fetchConversionRate("GBP", "AED"));
    cbuaeRateForNote =
      precomputedTotals.cbuaeRateForNote ?? cbuaeRateForNote;
    totalAed = toMoney(precomputedTotals.totalAed ?? totalAed);
    vatAedAmount = toMoney(precomputedTotals.vatAedAmount ?? vatAedAmount);
    totalIncVatAed = toMoney(
      precomputedTotals.totalIncVatAed ?? totalIncVatAed
    );
  } else {
    conversionRate = await timedAsync(
      "conversion rate fetch",
      () =>
        exchangeRateSource === "CBUAE"
          ? fetchCBUAEConversionRate()
          : fetchConversionRate("GBP", "AED")
    );
    const taskIncomeSum = toMoney(
      projectTasks.reduce((acc, t) => acc + toNumber(t.income), 0)
    );
    totalAmount =
      taskIncomeSum > 0 ? taskIncomeSum : toMoney(projectData.income);
    const vatFromTasks = toMoney(
      projectTasks.reduce((acc, t) => {
        const rate = toNumber(t.vat);
        if (rate <= 0) return acc;
        return acc + toNumber(t.income) * (rate / 100);
      }, 0)
    );
    vatAmount = vatFromTasks || toMoney(totalAmount * (fallbackVatRate / 100));
    labelVatRate =
      vatFromTasks && projectTasks.some((t) => toNumber(t.vat) > 0)
        ? toNumber(projectTasks.find((t) => toNumber(t.vat) > 0)?.vat)
        : fallbackVatRate;
    totalIncVat = toMoney(totalAmount + vatAmount);
    vatLabel = labelVatRate > 0 ? `VAT @ ${labelVatRate}%` : "VAT";

    if (selectedCompany === "UAE") {
      if (useManualAED) {
        totalAed = toMoney(
          projectTasks.reduce((acc, task) => acc + toNumber(task.incomeAED), 0)
        );
        const vatAedFromTasks = toMoney(
          projectTasks.reduce((acc, task) => {
            const rate =
              toNumber(task.vatAED ?? task.vat ?? fallbackVatRate) || 0;
            if (rate <= 0) return acc;
            return acc + toNumber(task.incomeAED) * (rate / 100);
          }, 0)
        );
        const manualIncVatSum = toMoney(
          projectTasks.reduce(
            (acc, task) => acc + toNumber(task.amountIncVatAED),
            0
          )
        );
        totalIncVatAed =
          manualIncVatSum > 0
            ? manualIncVatSum
            : toMoney(totalAed + vatAedFromTasks);
        vatAedAmount =
          vatAedFromTasks > 0
            ? vatAedFromTasks
            : toMoney(totalIncVatAed - totalAed);
      } else {
        totalAed = toMoney(totalAmount * conversionRate);
        vatAedAmount = toMoney(vatAmount * conversionRate);
        totalIncVatAed = toMoney(totalAed + vatAedAmount);
      }
    }
  }

  if (cbuaeRateForNote === null && selectedCompany === "UAE" && showGBP) {
    cbuaeRateForNote =
      exchangeRateSource === "CBUAE"
        ? conversionRate
        : await timedAsync("CBUAE rate fetch (note)", () =>
          fetchCBUAEConversionRate()
        );
  }

  const invoicingConfig = await timedAsync(
    "invoicing config fetch",
    fetchInvoicingConfig
  );
  const profile =
    invoicingConfig.profiles?.[selectedCompany] ||
    DEFAULT_INVOICING_CONFIG.profiles[selectedCompany] ||
    DEFAULT_INVOICING_CONFIG.profiles.UK;

  const invoiceNumber = invoiceNoOverride || projectData.invoiceNo;
  const invoiceFileName = `${invoiceNumber || projectData.projectRef || "invoice"
    }.pdf`;

  const logoUrl =
    selectedCompany === "UAE" && labelVatRate > 0 && profile.header.logoUrlTax
      ? profile.header.logoUrlTax
      : profile.header.logoUrl;
  const logoDataUrl = await timedAsync("logo fetch", () =>
    loadImageAsDataUrl(getAssetUrl(logoUrl))
  );

  const docPdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  const pageWidth = docPdf.internal.pageSize.getWidth();
  const pageHeight = docPdf.internal.pageSize.getHeight();
  const margin = 12.7; // mirror 0.5in margin used by html2pdf in v1
  const availableWidth = pageWidth - margin * 2;
  const containerWidth = availableWidth * 0.9; // template uses 90% width container
  const containerX = margin + (availableWidth - containerWidth) / 2;
  const containerPadding = pxToMm(20);
  const contentX = containerX + containerPadding;
  const contentWidth = containerWidth - containerPadding * 2;
  const topPadding = containerPadding;
  const topMargin = pxToMm(10);
  let cursorY = margin + topMargin + topPadding;

  const bodyFontPx = 12;
  const bodyFontSize = pxToPt(bodyFontPx);
  const bodyLineHeight = pxToMm(bodyFontPx * 1.75);
  const paragraphSpacing = pxToMm(6);
  const tableFontPx = 11;
  const tableFontSize = pxToPt(tableFontPx);
  const tableLineHeight = pxToMm(tableFontPx * 1.55);
  const headerRowHeight = pxToMm(19);
  const minRowHeight = headerRowHeight;

  const headerFill = [242, 242, 242];
  const lightRowFill = [245, 245, 245];
  const totalGreen = [0, 192, 142];
  const gridColor = [221, 221, 221];

  docPdf.setFont("helvetica", "normal");
  docPdf.setFontSize(bodyFontSize);

  const taskDescriptionMap = {
    DSS: "Director-Shareholder Search",
    SR: "Screening Report",
    BC: "Background Checks",
    PS: "Property Ownership Enquiries",
    VS: "Vehicle Ownership Enquiries",
    IMEQ: "Immigration Enquiries",
    BEQ: "Banking Relationship Enquiries",
    FL: "Corporate Records Report",
    SS: "Subsidiary Search",
    EQ: "Human Source Enquiries",
    LC: "Litigation Enquiries",
    MA: "Media Archive Search",
    RPS: "Reverse Property Ownership Enquiries",
    SV: "Site Visit",
    VES: "Vessle Ownership Enquiries",
  };

  const columnDefs = (() => {
    const hasGbp = !!showGBP;
    const hasAed = selectedCompany === "UAE";
    const hasProject = !!includeProjectColumn;

    if (hasProject && hasGbp && hasAed) {
      return [
        { key: "idx", label: "#", weight: 0.06 },
        { key: "project", label: "Project", weight: 0.16 },
        { key: "subject", label: "Subject", weight: 0.22 },
        { key: "requirements", label: "Requirements", weight: 0.28 },
        { key: "feeGbp", label: "Fee (GBP)", weight: 0.14 },
        { key: "feeAed", label: "Fee (AED)", weight: 0.14 },
      ];
    }
    if (hasProject && hasGbp) {
      return [
        { key: "idx", label: "#", weight: 0.07 },
        { key: "project", label: "Project", weight: 0.18 },
        { key: "subject", label: "Subject", weight: 0.24 },
        { key: "requirements", label: "Requirements", weight: 0.33 },
        { key: "feeGbp", label: "Fee (GBP)", weight: 0.18 },
      ];
    }
    if (hasProject && hasAed) {
      return [
        { key: "idx", label: "#", weight: 0.08 },
        { key: "project", label: "Project", weight: 0.2 },
        { key: "subject", label: "Subject", weight: 0.24 },
        { key: "requirements", label: "Requirements", weight: 0.3 },
        { key: "feeAed", label: "Fee (AED)", weight: 0.18 },
      ];
    }

    if (hasGbp && hasAed) {
      return [
        { key: "idx", label: "#", weight: 0.07 },
        { key: "subject", label: "Subject", weight: 0.27 },
        { key: "requirements", label: "Requirements", weight: 0.35 },
        { key: "feeGbp", label: "Fee (GBP)", weight: 0.15 },
        { key: "feeAed", label: "Fee (AED)", weight: 0.16 },
      ];
    }
    if (hasGbp) {
      return [
        { key: "idx", label: "#", weight: 0.07 },
        { key: "subject", label: "Subject", weight: 0.3 },
        { key: "requirements", label: "Requirements", weight: 0.46 },
        { key: "feeGbp", label: "Fee (GBP)", weight: 0.17 },
      ];
    }
    if (hasAed) {
      return [
        { key: "idx", label: "#", weight: 0.08 },
        { key: "subject", label: "Subject", weight: 0.31 },
        { key: "requirements", label: "Requirements", weight: 0.43 },
        { key: "feeAed", label: "Fee (AED)", weight: 0.18 },
      ];
    }
    return [
      { key: "idx", label: "#", weight: 0.1 },
      { key: "subject", label: "Subject", weight: 0.45 },
      { key: "requirements", label: "Requirements", weight: 0.45 },
    ];
  })().map((col) => ({
    ...col,
    width: col.weight * contentWidth,
  }));

  const drawLines = (lines, x, startY, lineHeight, options = {}) => {
    let y = startY;
    lines.forEach((line) => {
      docPdf.text(line, x, y, { baseline: "top", ...options });
      y += lineHeight;
    });
    return y;
  };

  const getRowHeight = (cells) => {
    const padding = pxToMm(4);
    let rowHeight = minRowHeight;
    columnDefs.forEach((col) => {
      const value = cells[col.key] ?? "";
      const lines = splitLines(docPdf, String(value), col.width - padding * 2);
      const cellHeight =
        measureLinesHeight(lines.length, tableLineHeight) + padding * 2;
      rowHeight = Math.max(rowHeight, cellHeight);
    });
    return rowHeight;
  };

  const drawTableHeader = () => {
    let x = contentX;
    docPdf.setFontSize(tableFontSize);
    docPdf.setFont("helvetica", "bold");
    docPdf.setFillColor(...headerFill);
    docPdf.setDrawColor(...gridColor);
    docPdf.setTextColor(0, 0, 0);
    columnDefs.forEach((col) => {
      docPdf.setFillColor(...headerFill);
      docPdf.rect(x, cursorY, col.width, headerRowHeight, "FD");
      docPdf.text(col.label, x + col.width / 2, cursorY + headerRowHeight / 2, {
        align: "center",
        baseline: "middle",
      });
      x += col.width;
    });
    cursorY += headerRowHeight;
    docPdf.setFont("helvetica", "normal");
  };

  const ensureSpaceFor = (neededHeight) => {
    if (cursorY + neededHeight > pageHeight - margin) {
      docPdf.addPage();
      cursorY = margin + topMargin + topPadding;
      drawTableHeader();
    }
  };

  const drawRow = (cells, opts = {}) => {
    const padding = pxToMm(4);
    let rowHeight = minRowHeight;
    docPdf.setFontSize(tableFontSize);

    const cellPayload = columnDefs.map((col) => {
      const value = cells[col.key] ?? "";
      const lines = splitLines(docPdf, String(value), col.width - padding * 2);
      const cellHeight =
        measureLinesHeight(lines.length, tableLineHeight) + padding * 2;
      rowHeight = Math.max(rowHeight, cellHeight);
      return { col, lines };
    });

    ensureSpaceFor(rowHeight);

    if (opts.textColor) docPdf.setTextColor(...opts.textColor);
    if (opts.fontStyle) docPdf.setFont("helvetica", opts.fontStyle);

    const omitLeadingColumns = opts.omitLeadingColumns ?? (opts.omitLeadingBorders ? 2 : 0);
    let x = contentX;
    docPdf.setDrawColor(...gridColor);
    cellPayload.forEach(({ col, lines }, idx) => {
      const skipBorder = omitLeadingColumns > 0 && idx < omitLeadingColumns;
      const shouldFill =
        opts.fillColor &&
        (!opts.fillColumns || opts.fillColumns.includes(col.key));
      if (shouldFill) {
        docPdf.setFillColor(...opts.fillColor);
        docPdf.rect(x, cursorY, col.width, rowHeight, skipBorder ? "F" : "FD");
      } else if (!skipBorder) {
        docPdf.rect(x, cursorY, col.width, rowHeight);
      }

      const textHeight = measureLinesHeight(lines.length, tableLineHeight);
      const textY =
        cursorY + (rowHeight - textHeight) / 2 + tableLineHeight / 2;
      lines.forEach((line, idx) => {
        docPdf.text(line, x + col.width / 2, textY + idx * tableLineHeight, {
          align: "center",
          baseline: "middle",
        });
      });
      x += col.width;
    });

    cursorY += rowHeight;
    docPdf.setTextColor(0, 0, 0);
    docPdf.setFont("helvetica", "normal");
  };

  if (logoDataUrl) {
    const props = docPdf.getImageProperties(logoDataUrl);
    const logoWidth = contentWidth;
    const logoHeight = (props.height / props.width) * logoWidth;
    docPdf.addImage(
      logoDataUrl,
      props.fileType || "PNG",
      contentX,
      cursorY,
      logoWidth,
      logoHeight
    );
    cursorY += logoHeight + pxToMm(20);
  }

  const leftWidth = contentWidth * 0.45;
  const rightWidth = contentWidth * 0.45;
  const rightPadding = pxToMm(90);
  const rightX = contentX + contentWidth - rightWidth + rightPadding;
  const adjustedRightWidth = Math.max(
    rightWidth - rightPadding,
    contentWidth * 0.2
  );

  const detailStartY = cursorY;
  docPdf.setFont("helvetica", "bold");
  docPdf.text(safeText(clientData.clientName || ""), contentX, detailStartY, {
    baseline: "top",
  });
  let leftY = detailStartY + bodyLineHeight;
  docPdf.setFont("helvetica", "normal");
  const clientLines = splitLines(docPdf, clientAddress, leftWidth);
  leftY = drawLines(clientLines, contentX, leftY, bodyLineHeight);
  const leftHeight = leftY - detailStartY + pxToMm(20);

  docPdf.setFont("helvetica", "bold");
  docPdf.text(safeText(profile.company?.name || ""), rightX, detailStartY, {
    baseline: "top",
  });
  let rightY = detailStartY + bodyLineHeight;
  docPdf.setFont("helvetica", "normal");
  (profile.company?.addressLines || []).forEach((line) => {
    const lines = splitLines(docPdf, line, adjustedRightWidth);
    rightY = drawLines(lines, rightX, rightY, bodyLineHeight);
  });
  if (profile.company?.phone) {
    docPdf.text(safeText(`Tel: ${profile.company.phone}`), rightX, rightY, {
      baseline: "top",
    });
    rightY += bodyLineHeight;
  }
  if (profile.company?.email) {
    docPdf.text(safeText(`Email: ${profile.company.email}`), rightX, rightY, {
      baseline: "top",
    });
    rightY += bodyLineHeight;
  }
  if (profile.company?.trn) {
    docPdf.text(safeText(`TRN: ${profile.company.trn}`), rightX, rightY, {
      baseline: "top",
    });
    rightY += bodyLineHeight;
  }
  rightY += pxToMm(20);

  const addLabelValue = (labelKey, value) => {
    const label = profile.invoiceLabels?.[labelKey];
    if (!label || (!value && value !== 0)) return;
    docPdf.setFont("helvetica", "bold");
    const labelText = `${label}: `;
    docPdf.text(safeText(labelText), rightX, rightY, { baseline: "top" });
    const labelWidth = docPdf.getTextWidth(labelText);
    docPdf.setFont("helvetica", "normal");
    const lines = splitLines(
      docPdf,
      String(value),
      adjustedRightWidth - labelWidth
    );
    rightY = drawLines(lines, rightX + labelWidth, rightY, bodyLineHeight);
  };

  addLabelValue(
    "invoiceDate",
    formattedDate ? formattedDate.replace(/\//g, "-") : ""
  );
  addLabelValue("invoiceNo", invoiceNumber);
  if (!includeProjectColumn) {
    addLabelValue("projectNo", projectData.projectRef);
  }
  addLabelValue("projectRef", projectData.paymentNotes);
  addLabelValue("orderedBy", projectData.poc);
  const rightHeight = rightY - detailStartY + pxToMm(20);

  cursorY = detailStartY + Math.max(leftHeight, rightHeight) + pxToMm(20);

  drawTableHeader();

  const totalsBase = {
    idx: "",
    subject: "",
    requirements: "Total",
    ...(includeProjectColumn ? { project: "" } : {}),
  };
  const vatBase = { ...totalsBase, requirements: vatLabel };
  const totalIncBase = { ...totalsBase, requirements: "Total Inc. VAT" };

  if (showGBP) {
    totalsBase.feeGbp = formatMoney(totalAmount).replace(".00", "");
    vatBase.feeGbp = formatMoney(vatAmount).replace(".00", "");
    totalIncBase.feeGbp = formatMoney(totalIncVat).replace(".00", "");
  }
  if (selectedCompany === "UAE") {
    totalsBase.feeAed = formatMoneyWithoutPoundSign(totalAed).replace(
      ".00",
      ""
    );
    vatBase.feeAed = formatMoneyWithoutPoundSign(vatAedAmount).replace(
      ".00",
      ""
    );
    totalIncBase.feeAed = formatMoneyWithoutPoundSign(totalIncVatAed).replace(
      ".00",
      ""
    );
  }

  const totalsRows = [totalsBase, vatBase, totalIncBase];
  const totalsBlockHeight = totalsRows.reduce(
    (acc, row, idx) =>
      acc +
      getRowHeight(row) +
      (idx === totalsRows.length - 1 ? 0 : 0),
    0
  );

  const tasks = projectTasks;
  const renderTasksStart = nowMs();
  tasks.forEach((task, index) => {
    const subject =
      invoiceChoice === "Anonymise subject"
        ? "Subject of Interest"
        : task.subject;
    const countryText = task.country ? ` (${task.country})` : "";
    const taskCode = task.task || "";
    const taskDescription = taskDescriptionMap[taskCode]
      ? `${taskDescriptionMap[taskCode]}${countryText}`
      : `${taskCode}${countryText}`;

    const feeAED =
      selectedCompany === "UAE"
        ? useManualAED && task.incomeAED
          ? parseFloat(task.incomeAED).toFixed(2)
          : (Number(task.income || 0) * conversionRate).toFixed(2)
        : null;

    const row = {
      idx: `${index + 1}`,
      subject,
      requirements: taskDescription,
    };
    if (includeProjectColumn) {
      row.project =
        task.projectLabel || task.projectRef || task.projectNo || "";
    }
    if (showGBP) {
      row.feeGbp = formatMoney(task.income || 0).replace(".00", "");
    }
    if (selectedCompany === "UAE") {
      row.feeAed = formatMoneyWithoutPoundSign(feeAED || 0).replace(".00", "");
    }

    const rowHeight = getRowHeight(row);
    if (cursorY + rowHeight + totalsBlockHeight > pageHeight - margin) {
      docPdf.addPage();
      cursorY = margin + topMargin + topPadding;
      drawTableHeader();
    }
    drawRow(row);
  });
  console.info(
    `[invoice-v2] rendered ${tasks.length} tasks in ${(
      nowMs() - renderTasksStart
    ).toFixed(1)}ms`
  );

  if (cursorY + totalsBlockHeight > pageHeight - margin) {
    docPdf.addPage();
    cursorY = margin + topMargin + topPadding;
    drawTableHeader();
  }

  drawRow(totalsBase, {
    omitLeadingBorders: true,
    omitLeadingColumns: includeProjectColumn ? 3 : 2,
  });
  drawRow(vatBase, {
    omitLeadingBorders: true,
    omitLeadingColumns: includeProjectColumn ? 3 : 2,
  });
  drawRow(totalIncBase, {
    fillColor: totalGreen,
    textColor: [255, 255, 255],
    fontStyle: "bold",
    omitLeadingBorders: true,
    omitLeadingColumns: includeProjectColumn ? 3 : 2,
    fillColumns: columnDefs
      .filter(
        (col) =>
          col.key !== "idx" &&
          col.key !== "subject" &&
          col.key !== "project"
      )
      .map((col) => col.key),
  });

  cursorY += pxToMm(10);

  const paymentHeading = profile.payment?.heading || "Payment Details";
  const cbuaeNote =
    selectedCompany === "UAE" && cbuaeRateForNote
      ? (() => {
        const numericRate = toNumber(cbuaeRateForNote);
        if (!Number.isFinite(numericRate) || numericRate <= 0) return null;
        return `Note: A Central Bank of the U.A.E. rate of ${numericRate.toFixed(
          5
        )} AED per GBP was applied at the time this invoice was generated. Any queries about this invoice should be raised within seven days of its receipt.`;
      })()
      : null;

  const paymentLines = [
    `Account Holder: ${profile.payment?.accountHolder || ""}`,
    ...(profile.payment?.bankLines || []),
    ...(selectedCompany === "UAE"
      ? []
      : [`Sort Code: ${profile.payment?.sortCode || ""}`]),
    `Account No: ${profile.payment?.accountNo || ""}`,
    `IBAN: ${profile.payment?.iban || ""}`,
    `SWIFT: ${profile.payment?.swift || ""}`,
    `Terms of Payment: ${profile.payment?.terms || ""}`,
    cbuaeNote || profile.payment?.note || "",
  ].filter(Boolean);

  const footerLines =
    selectedCompany === "UAE"
      ? [
        [profile.footer?.line, profile.footer?.contact]
          .filter(Boolean)
          .join(" | "),
      ].filter(Boolean)
      : [profile.footer?.line || "", profile.footer?.contact || ""].filter(
        Boolean
      );

  const footerFontPx = 8;
  const footerFontSize = pxToPt(footerFontPx);
  const footerLineHeight = pxToMm(footerFontPx * 1.35);
  const footerTopGap = pxToMm(20);

  const measureBlockHeight = (
    lines,
    fontSize,
    lineHeight,
    spacing = paragraphSpacing
  ) => {
    const previousSize = docPdf.getFontSize();
    docPdf.setFontSize(fontSize);
    const total = lines.reduce((acc, line) => {
      const split = splitLines(docPdf, line, contentWidth);
      return acc + measureLinesHeight(split.length, lineHeight) + spacing;
    }, 0);
    docPdf.setFontSize(previousSize);
    return total > 0 ? total - spacing : 0;
  };

  const paymentHeight =
    bodyLineHeight +
    paragraphSpacing +
    measureBlockHeight(
      paymentLines,
      bodyFontSize,
      bodyLineHeight,
      paragraphSpacing
    );
  const footerHeight = measureBlockHeight(
    footerLines,
    footerFontSize,
    footerLineHeight,
    0
  );

  const spaceNeededForFooter = footerHeight + footerTopGap;
  if (cursorY + paymentHeight + spaceNeededForFooter > pageHeight - margin) {
    docPdf.addPage();
    cursorY = margin + topMargin + topPadding;
  }

  docPdf.setFontSize(bodyFontSize);
  docPdf.setFont("helvetica", "bold");
  docPdf.text(paymentHeading, contentX, cursorY, { baseline: "top" });
  docPdf.setFont("helvetica", "normal");
  cursorY += bodyLineHeight + paragraphSpacing;
  paymentLines.forEach((line) => {
    const lines = splitLines(docPdf, line, contentWidth);
    cursorY = drawLines(lines, contentX, cursorY, bodyLineHeight);
    cursorY += paragraphSpacing;
  });

  docPdf.setFontSize(footerFontSize);
  docPdf.setFont("helvetica", "normal");
  let footerStartY = pageHeight - margin - footerHeight;
  if (footerStartY <= cursorY) {
    docPdf.addPage();
    cursorY = margin + topMargin + topPadding;
    footerStartY = pageHeight - margin - footerHeight;
  }
  cursorY = footerStartY;
  footerLines.forEach((line) => {
    const lines = splitLines(docPdf, line, contentWidth);
    cursorY = drawLines(
      lines,
      contentX + contentWidth / 2,
      cursorY,
      footerLineHeight,
      {
        align: "center",
      }
    );
  });

  const pdfBlob = await timedAsync("pdf blob output", () =>
    Promise.resolve(docPdf.output("blob"))
  );
  const timestamp = Date.now();
  const uniqueId = generateShortUUID();
  const storageBase = storageFolder || projectId || "combined";
  const safeName = sanitizeForFileName(
    invoiceNumber || projectData.projectRef || "invoice"
  );
  // Include uniqueId to prevent file overwrites that invalidate download tokens
  const storagePath = `invoices/${storageBase}/${timestamp}-${uniqueId}-${safeName}.pdf`;
  const storageRef = ref(storage, storagePath);

  const metadata = {
    contentType: "application/pdf",
    customMetadata: {
      projectId,
      projectRef: projectData.projectRef || "",
      invoiceNo: invoiceNumber || "",
      selectedCompany,
      exchangeRateSource,
      showGBP: String(showGBP),
      useManualAED: String(useManualAED),
      version: "v2",
      ...(extraMetadata?.customMetadata || {}),
    },
  };

  const uploadTask = uploadBytesResumable(storageRef, pdfBlob, metadata);
  const invoiceRecordPromise = (async () => {
    await timedAsync("storage upload", () =>
      new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, resolve);
      })
    );
    const downloadURL = await timedAsync("storage getDownloadURL", () =>
      getDownloadURL(uploadTask.snapshot.ref)
    );

    const invoiceRecord = {
      name: invoiceFileName,
      url: downloadURL,
      path: storagePath,
      size: pdfBlob.size,
      contentType: "application/pdf",
      createdAt: new Date(),
      createdAtMs: Date.now(),
      generatedAt: new Date(),
      selectedCompany,
      exchangeRateSource,
      showGBP,
      useManualAED,
      vatRate: labelVatRate || null,
      amountIncVat: projectData.amountIncVat || null,
      version: "v2",
      ...extraMetadata,
    };
    return invoiceRecord;
  })().catch((err) => {
    console.error("[invoice-v2] upload failed", err);
    throw err;
  });

  const downloadUrl = window.URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = invoiceFileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(downloadUrl);

  console.info(
    `[invoice-v2] total pre-upload time ${(
      nowMs() - overallStart
    ).toFixed(1)}ms for projectId=${projectId || "unknown"} (upload continues in background)`
  );
  return { invoiceRecordPromise };
};

export const generateInvoiceV2 = async ({
  projectId,
  invoiceChoice,
  selectedCompany,
  formatMoney,
  formatMoneyWithoutPoundSign,
  useManualAED = false,
  showGBP = true,
  exchangeRateSource = "ExchangeRate-API",
}) => {
  const projectDoc = await timedAsync("project fetch", () =>
    getDoc(doc(db, "projects", projectId))
  );
  if (!projectDoc.exists()) {
    console.error("No such project document!");
    return;
  }
  const projectData = projectDoc.data();

  const clientData = await timedAsync("client fetch", () =>
    fetchClientByName(projectData.client)
  );

  const { invoiceRecordPromise } = await renderInvoicePdf({
    projectId,
    projectData,
    clientData,
    invoiceChoice,
    selectedCompany,
    formatMoney,
    formatMoneyWithoutPoundSign,
    useManualAED,
    showGBP,
    exchangeRateSource,
  });

  const projectRef = doc(db, "projects", projectId);
  const backgroundUpload = (async () => {
    const invoiceRecord = await invoiceRecordPromise;
    // Use arrayUnion with retries to avoid losing references on transient failures
    await retryWithBackoff("project invoice list update", () =>
      timedAsync("project invoice list update", () =>
        updateDoc(projectRef, { invoices: arrayUnion(invoiceRecord) })
      )
    );
    return invoiceRecord;
  })().catch((err) => {
    console.error("[invoice-v2] upload/update failed", err);
    throw err;
  });

  // Return immediately with a handle to the background work for optional tracking.
  return { uploadPromise: backgroundUpload };
};

export const generateCombinedInvoiceV2 = async ({
  projectIds = [],
  invoiceChoice,
  selectedCompany,
  invoiceNoOverride,
  formatMoney,
  formatMoneyWithoutPoundSign,
  useManualAED = false,
  showGBP = true,
  exchangeRateSource = "ExchangeRate-API",
}) => {
  const uniqueIds = Array.from(new Set(projectIds.filter(Boolean)));
  if (uniqueIds.length < 2) {
    return generateInvoiceV2({
      projectId: uniqueIds[0],
      invoiceChoice,
      selectedCompany,
      formatMoney,
      formatMoneyWithoutPoundSign,
      useManualAED,
      showGBP,
      exchangeRateSource,
    });
  }

  const projectDocs = await timedAsync("combined project fetch", () =>
    Promise.all(uniqueIds.map((id) => getDoc(doc(db, "projects", id))))
  );

  const projects = projectDocs
    .filter((snap) => snap.exists())
    .map((snap) => ({ id: snap.id, data: snap.data() }));

  if (!projects.length) {
    console.error("No matching projects found for combined invoice");
    return;
  }

  const clients = Array.from(
    new Set(projects.map((p) => p.data.client).filter(Boolean))
  );
  if (clients.length > 1) {
    throw new Error("Selected projects belong to different clients.");
  }
  const clientData = await timedAsync("client fetch", () =>
    fetchClientByName(clients[0])
  );

  const projectRefs = projects
    .map((p) => p.data.projectRef || p.id)
    .filter(Boolean);
  const trimmedOverride =
    typeof invoiceNoOverride === "string" ? invoiceNoOverride.trim() : "";
  const invoiceNumber =
    trimmedOverride || generateCombinedInvoiceNumber(projectRefs);
  let latestInvoiceDate = null;
  projects.forEach((current) => {
    const currentDate = normalizeDateValue(current.data.dateForInvoice);
    if (!currentDate) return;
    if (!latestInvoiceDate || currentDate.getTime() > latestInvoiceDate.getTime()) {
      latestInvoiceDate = currentDate;
    }
  });

  let selectedPaymentNotes = "";
  let selectedPaymentNotesDate = null;
  projects.forEach((current) => {
    const notes = (current?.data?.paymentNotes || "").trim();
    if (!notes) return;
    const noteDate = normalizeDateValue(current.data.dateForInvoice);
    if (!selectedPaymentNotes) {
      selectedPaymentNotes = notes;
      selectedPaymentNotesDate = noteDate;
      return;
    }
    if (noteDate && selectedPaymentNotesDate) {
      if (noteDate.getTime() > selectedPaymentNotesDate.getTime()) {
        selectedPaymentNotes = notes;
        selectedPaymentNotesDate = noteDate;
      }
      return;
    }
    if (noteDate && !selectedPaymentNotesDate) {
      selectedPaymentNotes = notes;
      selectedPaymentNotesDate = noteDate;
      return;
    }
    if (!noteDate && !selectedPaymentNotesDate) {
      selectedPaymentNotes = notes;
    }
  });

  const aggregatedTasks = [];
  let totalAmount = 0;
  let vatAmount = 0;
  const vatRatesUsed = new Set();

  projects.forEach((project) => {
    const tasks = Array.isArray(project.data.tasks) ? project.data.tasks : [];
    const fallbackVatRate = toNumber(project.data.vat);
    const taskIncomeSum = toMoney(
      tasks.reduce((acc, t) => acc + toNumber(t.income), 0)
    );
    const projectTotalAmount =
      taskIncomeSum > 0 ? taskIncomeSum : toMoney(project.data.income);

    const vatFromTasks = toMoney(
      tasks.reduce((acc, t) => {
        const rate = toNumber(t.vat);
        if (rate <= 0) return acc;
        vatRatesUsed.add(rate);
        return acc + toNumber(t.income) * (rate / 100);
      }, 0)
    );

    const projectVatAmount =
      vatFromTasks || toMoney(projectTotalAmount * (fallbackVatRate / 100));
    if (vatFromTasks <= 0 && fallbackVatRate > 0) {
      vatRatesUsed.add(fallbackVatRate);
    }

    totalAmount += projectTotalAmount;
    vatAmount += projectVatAmount;

    tasks.forEach((task) => {
      aggregatedTasks.push({
        ...task,
        projectLabel: project.data.projectRef || project.id,
        projectRef: project.data.projectRef || project.id,
        projectNo: project.data.projectNo || "",
        _fallbackVat: fallbackVatRate,
      });
    });
  });

  const totalIncVat = toMoney(totalAmount + vatAmount);

  let labelVatRate = null;
  let vatLabel = "VAT";
  const vatRatesArray = Array.from(vatRatesUsed).filter(
    (rate) => toNumber(rate) > 0
  );
  if (vatRatesArray.length === 1) {
    labelVatRate = toNumber(vatRatesArray[0]);
    vatLabel = `VAT @ ${labelVatRate}%`;
  } else if (vatRatesArray.length > 1) {
    vatLabel = "VAT (mixed rates)";
    labelVatRate = toNumber(vatRatesArray[0]);
  } else {
    labelVatRate = 0;
  }

  const conversionRate =
    exchangeRateSource === "CBUAE"
      ? await timedAsync("conversion rate fetch", () =>
        fetchCBUAEConversionRate()
      )
      : await timedAsync("conversion rate fetch", () =>
        fetchConversionRate("GBP", "AED")
      );

  const cbuaeRateForNote =
    selectedCompany === "UAE" && showGBP
      ? exchangeRateSource === "CBUAE"
        ? conversionRate
        : await timedAsync("CBUAE rate fetch (note)", () =>
          fetchCBUAEConversionRate()
        )
      : null;

  let totalAed = 0;
  let vatAedAmount = 0;
  let totalIncVatAed = 0;
  if (selectedCompany === "UAE") {
    if (useManualAED) {
      totalAed = toMoney(
        aggregatedTasks.reduce((acc, task) => acc + toNumber(task.incomeAED), 0)
      );
      const vatAedFromTasks = toMoney(
        aggregatedTasks.reduce((acc, task) => {
          const rate =
            toNumber(task.vatAED ?? task.vat ?? task._fallbackVat) || 0;
          if (rate <= 0) return acc;
          return acc + toNumber(task.incomeAED) * (rate / 100);
        }, 0)
      );
      const manualIncVatSum = toMoney(
        aggregatedTasks.reduce(
          (acc, task) => acc + toNumber(task.amountIncVatAED),
          0
        )
      );
      totalIncVatAed =
        manualIncVatSum > 0
          ? manualIncVatSum
          : toMoney(totalAed + vatAedFromTasks);
      vatAedAmount =
        vatAedFromTasks > 0
          ? vatAedFromTasks
          : toMoney(totalIncVatAed - totalAed);
    } else {
      totalAed = toMoney(totalAmount * conversionRate);
      vatAedAmount = toMoney(vatAmount * conversionRate);
      totalIncVatAed = toMoney(totalAed + vatAedAmount);
    }
  }

  const combinedProjectData = {
    ...projects[0].data,
    projectRef: projectRefs.join(", "),
    paymentNotes: selectedPaymentNotes,
    poc:
      projects.find((p) => p.data.poc)?.data.poc ||
      projects[0].data.poc ||
      "",
    dateForInvoice: latestInvoiceDate || projects[0].data.dateForInvoice,
    vat: labelVatRate ?? projects[0].data.vat ?? 0,
    income: totalAmount,
    tasks: aggregatedTasks,
    invoiceNo: invoiceNumber,
    amountIncVat: totalIncVat,
    client: projects[0].data.client,
  };

  const { invoiceRecordPromise } = await renderInvoicePdf({
    projectId: uniqueIds[0],
    projectData: combinedProjectData,
    clientData,
    invoiceChoice,
    selectedCompany,
    formatMoney,
    formatMoneyWithoutPoundSign,
    useManualAED,
    showGBP,
    exchangeRateSource,
    includeProjectColumn: true,
    projectsIncluded: projects.map((p) => ({
      id: p.id,
      projectRef: p.data.projectRef,
    })),
    invoiceDateOverride: latestInvoiceDate,
    invoiceNoOverride: invoiceNumber,
    storageFolder: "combined",
    precomputedTotals: {
      totalAmount,
      vatAmount,
      totalIncVat,
      labelVatRate: labelVatRate ?? 0,
      vatLabel,
      conversionRate,
      cbuaeRateForNote,
      totalAed,
      vatAedAmount,
      totalIncVatAed,
    },
    extraMetadata: {
      isCombinedInvoice: true,
      combinedProjectIds: uniqueIds.join(","),
      invoiceAmountIncVat: totalIncVat,
      customMetadata: {
        combinedProjectIds: uniqueIds.join(","),
      },
    },
  });

  const backgroundUpload = invoiceRecordPromise
    .then(async (invoiceRecord) => {
      await Promise.all(
        projects.map(async (project) => {
          const projectRef = doc(db, "projects", project.id);
          // Use arrayUnion with retries to prevent losing invoice references
          await retryWithBackoff(
            `project invoice list update (${project.id})`,
            () =>
              timedAsync("project invoice list update", () =>
                updateDoc(projectRef, {
                  invoices: arrayUnion(invoiceRecord),
                })
              )
          );
        })
      );
      return invoiceRecord;
    })
    .catch((err) => {
      console.error("[invoice-v2] combined upload/update failed", err);
      throw err;
    });

  return { uploadPromise: backgroundUpload };
};

export default generateInvoiceV2;
